import type { IndexUiState, Router, UiState } from "instantsearch.js/es/types";
import { isEqual } from "lodash";
import type { Ref } from "vue";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

interface AlgoliaFacet {
  attribute: string;
  paramName: string;
}

interface SortConverters {
  toUrl: (sortBy?: string) => string | undefined;
  fromUrl: (urlValue?: string) => string | undefined;
}

interface UseAlgoliaQueryRouting {
  routing: Ref<{ router: Router<UiState> }>;
  setIndexName: (newIndexName?: string) => void;
  setAlgoliaFacets: (facetList?: AlgoliaFacet[]) => void;
  setSortConverters: (converters: SortConverters) => void;
}

/**
 * Provides query routing functionality for Algolia search.
 *
 * All state lives in function-scope refs so each call gets a fresh instance. The previous
 * module-level refs bled across remounts and across tests; consumers (today just
 * MyStudentsSearchComponent) now own their own routing closure.
 *
 * @see {@link https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/vue/#combining-with-vue-router|Vue Router}
 */
export const useAlgoliaQueryRouting = (): UseAlgoliaQueryRouting => {
  const route = useRoute();
  const router = useRouter();

  const indexName = ref<string>("");
  const algoliaFacets = ref<AlgoliaFacet[]>([]);
  const lastUiState = ref<UiState>({});
  const lastPath = ref<string>(route.path ?? "");
  const lastExchangeMode = ref<string | undefined>(undefined);

  // Pass-through converters until a domain composable (e.g. useMyStudentsAlgoliaIndex) registers
  // domain-aware versions via setSortConverters. Lets URLs stay clean (?sort=first_name_asc rather
  // than the full virtual-replica index name).
  const sortConverters = ref<SortConverters>({
    toUrl: (sortBy) => sortBy,
    fromUrl: (urlValue) => urlValue,
  });

  // Listener handles for cleanup in dispose(). Previously stored on `this._foo` of the
  // routing.router object; lifting them to closure refs keeps the Router shape strictly typed.
  let removeAfterEachListener: (() => void) | undefined;
  let popStateListener: (() => void) | undefined;

  const getUiState = (uiState: UiState): UiState => {
    const keys = Object.keys(uiState);
    if (keys.length === 0) {
      return {};
    }
    // After a sort-driven index swap, InstantSearch may hand us uiState
    // containing both the old and new index keys. Prefer the current
    // indexName; fall back to the most recently added key, not keys[0]
    // (which is the stale old index).
    const state = uiState[indexName.value] ?? uiState[keys[keys.length - 1]];
    return {
      [indexName.value]: { ...state },
    };
  };

  const exchangeModeChanged = (): boolean => {
    return route.query.exchange_mode !== lastExchangeMode.value;
  };

  const refinementListToRoute = (indexUiState: IndexUiState | undefined): Record<string, string | undefined> => {
    const refinementList: Record<string, string | undefined> = {};
    algoliaFacets.value.forEach((facet) => {
      const facetArray = indexUiState?.refinementList?.[facet.attribute];
      refinementList[facet.paramName] = facetArray
        ? facetArray.map((facetItem) => encodeURIComponent(facetItem)).join(",")
        : undefined;
    });
    return refinementList;
  };

  const routeToRefinementList = (): Record<string, string[]> => {
    const refinementList: Record<string, string[]> = {};
    algoliaFacets.value.forEach((facet) => {
      const raw = route.query[facet.paramName];
      if (typeof raw === "string" && raw.length > 0) {
        refinementList[facet.attribute] = raw.split(",").map((facetValue) => decodeURIComponent(facetValue));
      }
    });
    return refinementList;
  };

  const doStatesMatch = (currentState: IndexUiState): boolean => {
    // lastUiState is stored as { [indexName]: IndexUiState } by write(), so dig into the
    // indexed shape rather than reading a non-existent top-level `search`.
    const lastIndexState: IndexUiState = lastUiState.value[indexName.value] ?? {};
    if ((currentState.query ?? "") !== (lastIndexState.query ?? "")) {
      return false;
    }
    if (currentState.sortBy !== lastIndexState.sortBy) {
      return false;
    }
    const lastRefinementList = lastIndexState.refinementList ?? {};
    return algoliaFacets.value.every((facet) => {
      const facetArray = currentState.refinementList?.[facet.attribute];
      const lastFacetArray = lastRefinementList[facet.attribute] ?? [];
      if (facetArray) {
        if (facetArray.length !== lastFacetArray.length) {
          return false;
        }
        const facetMatch = lastFacetArray.every((facetCheck) => facetArray.includes(decodeURIComponent(facetCheck)));
        if (!facetMatch) return false;
      } else if (lastFacetArray.length > 0) {
        return false;
      }
      return true;
    });
  };

  const routing = ref<{ router: Router<UiState> }>({
    router: {
      read(): UiState {
        return {
          [indexName.value]: {
            query: route.query?.search ? String(route.query.search) : "",
            refinementList: routeToRefinementList(),
            sortBy: sortConverters.value.fromUrl(typeof route.query?.sort === "string" ? route.query.sort : undefined),
          },
        };
      },

      write(uiState: UiState): void {
        const state = getUiState(uiState);
        const indexState = state[indexName.value];
        const newQuery: Record<string, string | undefined> = {
          search: (indexState?.query ?? "").length > 0 ? indexState?.query : undefined,
          ...refinementListToRoute(indexState),
          sort: sortConverters.value.toUrl(indexState?.sortBy),
        };

        // Compare like to like: newQuery only ever contains `search` + the registered
        // facet param names + `sort`, so project route.query down to those same keys before
        // diffing. Otherwise unrelated params (approval_status, ts, tenant_entity_id, …)
        // make the two strings perpetually unequal and router.replace fires on every keystroke.
        const trackedKeys = ["search", ...algoliaFacets.value.map((facet) => facet.paramName), "sort"];
        const projectedRouteQuery = trackedKeys.reduce<Record<string, unknown>>((acc, key) => {
          if (route.query[key] !== undefined) {
            acc[key] = route.query[key];
          }
          return acc;
        }, {});

        const updatedQuery = JSON.stringify(newQuery);
        const routeQuery = JSON.stringify(projectedRouteQuery);

        // Keep lastExchangeMode in sync whenever the URL carries a mode, even if the
        // short-circuit below skips the write. Only update when present so we don't
        // clobber a previously-known mode with undefined.
        if (typeof route.query.exchange_mode === "string") {
          lastExchangeMode.value = route.query.exchange_mode;
        }

        if (!(isEqual(updatedQuery, routeQuery) && isEqual(lastPath.value, route.path))) {
          lastUiState.value = state;
          lastPath.value = route.path;
          router.replace({ query: { ...route.query, ...newQuery } });
        }
      },

      createURL(routeState: UiState): string {
        return router.resolve({ query: routeState as Record<string, string> }).href;
      },

      onUpdate(cb: (route: UiState) => void): void {
        if (typeof window === "undefined") return;

        removeAfterEachListener = router.afterEach(() => {
          const currentState = routing.value.router.read();
          if (!doStatesMatch(currentState[indexName.value] ?? {}) || exchangeModeChanged()) {
            cb(currentState);
          }
        });

        popStateListener = () => {
          const currentState = routing.value.router.read();
          if (!doStatesMatch(currentState[indexName.value] ?? {}) || exchangeModeChanged()) {
            cb(currentState);
          }
        };
        window.addEventListener("popstate", popStateListener);
      },

      dispose(): void {
        if (typeof window === "undefined") return;
        if (popStateListener) {
          window.removeEventListener("popstate", popStateListener);
          popStateListener = undefined;
        }
        if (removeAfterEachListener) {
          removeAfterEachListener();
          removeAfterEachListener = undefined;
        }
      },
    },
  });

  const setIndexName = (newIndexName: string = ""): void => {
    indexName.value = newIndexName;
  };

  const setAlgoliaFacets = (facetList: AlgoliaFacet[] = []): void => {
    algoliaFacets.value = facetList;
  };

  /**
   * Register domain-aware converters between InstantSearch's full sortBy index name and
   * the URL `?sort` param value. Called by domain composables (e.g. useMyStudentsAlgoliaIndex)
   * during search component setup.
   */
  const setSortConverters = (converters: SortConverters): void => {
    sortConverters.value = converters;
  };

  return { routing, setIndexName, setAlgoliaFacets, setSortConverters };
};
